// ChatWidget.tsx
import React, { useState, useEffect, useRef } from 'react';
import { CometChatService } from '../services/cometchat';
import { chatAPI } from '../services/api';
import './ChatWidget.css';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: number;
  isAnimated?: boolean;
  isAnimating?: boolean;
}

interface TypewriterTextProps {
  text: string;
  speed: number;
  onComplete: () => void;
}

const TypewriterText: React.FC<TypewriterTextProps> = ({ text, speed, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    } else if (currentIndex === text.length && onComplete) {
      onComplete();
    }
  }, [currentIndex, text, speed, onComplete]);

  return <>{displayedText}</>;
}

const formatMarkdown = (text: string): string => {
  let formatted = text
    // Bold and italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Headers
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    // Convert numbered lists to proper HTML
    .replace(/^(\d+)\. (.*$)/gm, '<li>$2</li>')
    // Convert bullet lists
    .replace(/^- (.*$)/gm, '<li>$1</li>');
  
  // Wrap consecutive <li> elements in proper list tags
  formatted = formatted.replace(/(<li>.*<\/li>\s*)+/gs, (match) => {
    const isNumbered = /^\d+\. /.test(text.match(/^\d+\. .*$/m)?.[0] || '');
    return isNumbered ? `<ol>${match}</ol>` : `<ul>${match}</ul>`;
  });
  
  return formatted
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n(?!<)/g, '<br>')
    .replace(/^(.*)$/, '<p>$1</p>');
};

const getRandomGreeting = () => {
  const greetings = [
    'مرحباً! 👋 أنا مساعدك الذكي. كيف يمكنني مساعدتك اليوم؟',
    'أهلاً بك! 🤖 مرحباً! بماذا يمكنني مساعدتك؟',
    'مرحباً! 🌟 أنا هنا للمساعدة. ما الذي يدور في ذهنك؟',
    'أهلاً وسهلاً! 🚀 مستعد للدردشة؟ اسألني أي شيء!',
    'تحياتي! ✨ أنا مساعدك الودود. كيف يمكنني خدمتك؟'
  ];
  return greetings[Math.floor(Math.random() * greetings.length)];
};

export const ChatWidget: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: getRandomGreeting(),
      sender: 'bot',
      timestamp: Date.now()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [conversationId, setConversationId] = useState<string>();
  const [userId] = useState(() => `user_${Date.now()}`);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<any>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);
  const currentAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const currentResponseIdRef = useRef<string | null>(null);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    initializeCometChat();
    return () => {
      disconnectVoice();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handlePopState = () => {
      if (isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  const initializeCometChat = async () => {
    try {
      const cometChat = CometChatService.getInstance();
      await cometChat.initialize();
      // Skip CometChat user creation for now - just use the chat API
    } catch (error) {
      console.error('CometChat initialization failed:', error);
    }
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
    });
  };

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      text: inputText,
      sender: 'user',
      timestamp: Date.now(),
      isAnimated: true,
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputText;
    setInputText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    const botMessageId = `bot_${Date.now()}`;
    let messageAdded = false;

    try {
      await chatAPI.sendMessageStream(
        {
          message: messageText,
          session_id: sessionId,
          conversationId,
          userId,
        },
        (chunk) => {
          if (!messageAdded) {
            // Add bot message on first chunk and hide loading
            setIsLoading(false);
            const botMessage: Message = {
              id: botMessageId,
              text: chunk,
              sender: 'bot',
              timestamp: Date.now(),
              isAnimated: true,
            };
            setMessages(prev => [...prev, botMessage]);
            messageAdded = true;
          } else {
            // Append to existing message
            setMessages(prev =>
              prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, text: msg.text + chunk }
                  : msg
              )
            );
          }
        },
        () => {
          setIsLoading(false);
        },
        (error) => {
          console.error('Stream error:', error);
          if (!messageAdded) {
            const errorMessage: Message = {
              id: botMessageId,
              text: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.',
              sender: 'bot',
              timestamp: Date.now(),
              isAnimated: true,
            };
            setMessages(prev => [...prev, errorMessage]);
          } else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === botMessageId
                  ? { ...msg, text: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' }
                  : msg
              )
            );
          }
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      if (!messageAdded) {
        const errorMessage: Message = {
          id: botMessageId,
          text: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.',
          sender: 'bot',
          timestamp: Date.now(),
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputText(value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      
      // Detect text direction
      const isArabic = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(value);
      textareaRef.current.style.direction = isArabic ? 'rtl' : 'ltr';
      textareaRef.current.style.textAlign = isArabic ? 'right' : 'left';
    }
  };

  const handleOpen = () => {
    setIsAnimating(true);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsAnimating(false);
    
    // Disconnect voice chat if active
    if (isVoiceMode || isConnected) {
      disconnectVoice();
    }
    
    setTimeout(() => setIsOpen(false), 300);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      // Step 1: Transcribe audio only
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const transcriptionResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (!transcriptionResponse.ok) {
        throw new Error('Transcription failed');
      }

      const { transcription } = await transcriptionResponse.json();

      // Step 2: Show user message immediately
      const userMessage: Message = {
        id: `user_${Date.now()}`,
        text: transcription,
        sender: 'user',
        timestamp: Date.now(),
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      // Step 3: Send transcription to chat API for AI response
      const chatResponse = await chatAPI.sendMessage({
        message: transcription,
        session_id: sessionId,
        conversationId,
        userId,
      });

      setConversationId(chatResponse.conversationId);

      // Step 4: Show bot response with animation
      const botMessage: Message = {
        id: `bot_${chatResponse.timestamp}`,
        text: chatResponse.response,
        sender: 'bot',
        timestamp: chatResponse.timestamp,
        isAnimated: true,
      };

      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error('Failed to transcribe audio:', error);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: 'عذراً، لم أتمكن من معالجة رسالتك الصوتية.',
        sender: 'bot',
        timestamp: Date.now(),
        isAnimated: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Voice Chat Functions
  const connectVoice = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const wsBase = apiUrl.replace(/^http/, 'ws');
      const wsUrl = `${wsBase}/voice${sessionId ? `?session_id=${sessionId}` : ''}`;
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        setIsConnected(true);
        setIsVoiceMode(true);

        // Ensure we have an AudioContext created under a user gesture
        if (!audioContextRef.current) {
          try {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) {
              throw new Error('AudioContext not supported in this browser');
            }
            audioContextRef.current = new AudioContextClass({ sampleRate: 24000 });
            // resume it once to satisfy autoplay policies
            if (audioContextRef.current.state === 'suspended') {
              await audioContextRef.current.resume();
            }
            console.log('AudioContext created successfully');
          } catch (e) {
            console.error('AudioContext creation/resume failed:', e);
            audioContextRef.current = null;
            throw new Error('Failed to initialize audio system');
          }
        }

        // Auto-start recording for continuous listening after a short delay
        setTimeout(() => startVoiceRecording(), 500);
      };

      ws.onmessage = async (event) => {
        try {
          const raw = event.data;
          const data = JSON.parse(raw);

          // track response id for interruption
          if (data.type === 'response.created' || data.type === 'response') {
            const respId = data.response?.id || data.response_id || data.response?.response_id;
            if (respId) currentResponseIdRef.current = respId;
          }

          // Accept either data.delta (string) or data.delta.audio
          if (data.type === 'response.audio.delta') {
            // delta might be an object with .audio or a raw base64 string
            let audioBase64: string | null = null;
            if (typeof data.delta === 'string') {
              audioBase64 = data.delta;
            } else if (data.delta && typeof data.delta === 'object') {
              audioBase64 = (data.delta as any).audio ?? (data.delta as any).data ?? null;
            } else if (data.audio) {
              audioBase64 = data.audio;
            }

            if (audioBase64) {
              const audioBuffer = base64ToArrayBuffer(audioBase64);
              audioQueueRef.current.push(audioBuffer);
              if (!isPlayingRef.current) {
                playAudioQueue();
              }
            }
          }

          // Some implementations emit start/done events — handle both
          if (data.type === 'response.audio.started' || data.type === 'response.audio.begin' || data.type === 'response.audio_start') {
            setIsSpeaking(true);
            // Clear any queued input audio when bot starts speaking
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'input_audio_buffer.clear'
              }));
            }
          }

          if (data.type === 'response.audio.done' || data.type === 'response.done' || data.type === 'response.audio.completed') {
            // Don't immediately stop - let audio queue finish playing
            setTimeout(() => {
              if (audioQueueRef.current.length === 0) {
                setIsSpeaking(false);
                isPlayingRef.current = false;
                currentResponseIdRef.current = null;
              }
            }, 100);
          }

          if (data.type === 'response.cancelled' || data.type === 'response.cancel') {
            setIsSpeaking(false);
            isPlayingRef.current = false;
            currentResponseIdRef.current = null;
            audioQueueRef.current = [];
            stopPlayback();
          }

          // handle raw transcripts from OpenAI if present
          if (data.type === 'conversation.item.input_audio_transcription.completed') {
            // Don't show user transcripts in voice mode - keep chat clean
            console.log('User said:', data.transcript);
          }

        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsVoiceMode(false);
        setIsRecording(false);
        setIsSpeaking(false);
        isPlayingRef.current = false;
        currentResponseIdRef.current = null;
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        setIsVoiceMode(false);
        setIsRecording(false);
        setIsSpeaking(false);
        // Show user-friendly error message
        alert('فشل في الاتصال الصوتي. يرجى التحقق من الاتصال بالإنترنت والمحاولة مرة أخرى.');
      };

    } catch (error) {
      console.error('Failed to connect voice:', error);
    }
  };

  const handleUserInterrupt = () => {
    console.log('Interrupt triggered, isSpeaking:', isSpeaking);
    
    // Clear debounce timeout if exists
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Stop all audio immediately
    stopPlayback();
    
    // Send cancellation to backend
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const cancelMessage: any = {
        type: 'response.cancel'
      };
      if (currentResponseIdRef.current) {
        cancelMessage.response_id = currentResponseIdRef.current;
      }
      console.log('Sending cancel message:', cancelMessage);
      wsRef.current.send(JSON.stringify(cancelMessage));
      
      // Clear input buffer to prevent queued responses
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.clear'
          }));
        }
      }, 100);
    }

    // Reset state
    currentResponseIdRef.current = null;
    setIsSpeaking(false);
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        try {
          await audioContextRef.current.resume();
        } catch (_) {}
      }

      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      // Use ScriptProcessor (deprecated but widely supported)
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer.getChannelData(0);

        // Calculate RMS for debugging
        let sum = 0;
        for (let i = 0; i < inputBuffer.length; i++) sum += inputBuffer[i] * inputBuffer[i];
        const rms = Math.sqrt(sum / inputBuffer.length);
        
        // Debug: Log when audio is detected (less frequently)
        if (rms > 0.001 && Math.random() < 0.1) {
          console.log(`Audio detected: ${rms.toFixed(4)}`);
        }

        // Send audio but handle interruption properly
        try {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            const pcmBuffer = new Int16Array(inputBuffer.length);
            for (let i = 0; i < inputBuffer.length; i++) {
              pcmBuffer[i] = Math.max(-32768, Math.min(32767, inputBuffer[i] * 32768));
            }

            const base64Audio = arrayBufferToBase64(pcmBuffer.buffer);

            // Handle user interruption immediately
            if (isSpeaking && rms > 0.02) {
              console.log('Voice interruption detected immediately, RMS:', rms);
              handleUserInterrupt();
            }
            
            // Always send audio - let server handle VAD
            wsRef.current.send(JSON.stringify({
              type: 'input_audio_buffer.append',
              audio: base64Audio
            }));
          }
        } catch (err) {
          console.error('Failed to send audio chunk:', err);
        }
      };

      source.connect(processor);
      // Connect to a gain node with zero volume to avoid feedback but keep processor active
      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 0; // Mute the output
      processor.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      mediaRecorderRef.current = { stream, source, processor, gainNode } as any;
      setIsRecording(true);

    } catch (error) {
      console.error('Failed to start voice recording:', error);
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current) {
      const { stream, source, processor, gainNode } = mediaRecorderRef.current as any;

      if (source) try { source.disconnect(); } catch (_) {}
      if (processor) try { processor.disconnect(); } catch (_) {}
      if (gainNode) try { gainNode.disconnect(); } catch (_) {}
      if (stream) stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());

      setIsRecording(false);
      mediaRecorderRef.current = null;

      // Let server-side VAD handle response creation automatically
    }
  };

  const playAudioQueue = async () => {
    if (audioQueueRef.current.length === 0 || isPlayingRef.current) return;

    isPlayingRef.current = true;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      }
      // Ensure the context is resumed (important for autoplay policies)
      try {
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
      } catch (e) {
        console.warn('AudioContext resume error:', e);
      }

      // Use the shared audioContextRef for playback
      const playbackContext = audioContextRef.current;

      while (audioQueueRef.current.length > 0 && isPlayingRef.current) {
        const audioBuffer = audioQueueRef.current.shift()!;
        try {
          // audioBuffer is an ArrayBuffer of PCM16 little-endian
          const pcmData = new Int16Array(audioBuffer);
          const buffer = playbackContext.createBuffer(1, pcmData.length, 24000);
          const channelData = buffer.getChannelData(0);

          for (let i = 0; i < pcmData.length; i++) {
            channelData[i] = pcmData[i] / 32768.0;
          }

          const audioBufferSource = playbackContext.createBufferSource();
          currentAudioSourceRef.current = audioBufferSource;
          audioBufferSource.buffer = buffer;
          audioBufferSource.connect(playbackContext.destination);

          await new Promise<void>((resolve) => {
            audioBufferSource.onended = () => {
              currentAudioSourceRef.current = null;
              resolve();
            };
            audioBufferSource.start();
          });
        } catch (bufferError) {
          console.error('Error processing audio buffer:', bufferError);
        }
      }
    } catch (error) {
      console.error('Error playing audio:', error);
    } finally {
      isPlayingRef.current = false;
      currentAudioSourceRef.current = null;
    }
  };

  const stopPlayback = () => {
    try {
      if (currentAudioSourceRef.current) {
        try {
          currentAudioSourceRef.current.stop();
        } catch (_) {}
        try {
          currentAudioSourceRef.current.disconnect();
        } catch (_) {}
        currentAudioSourceRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping playback:', err);
    } finally {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      audioQueueRef.current = [];
    }
  };

  const disconnectVoice = () => {
    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (isRecording) {
      stopVoiceRecording();
    }

    if (currentAudioSourceRef.current) {
      try {
        currentAudioSourceRef.current.stop();
      } catch (e) {
        // Audio source might already be stopped
      }
      currentAudioSourceRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // ignore
      }
      audioContextRef.current = null;
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    currentResponseIdRef.current = null;
    setIsConnected(false);
    setIsVoiceMode(false);
    setIsRecording(false);
    setIsSpeaking(false);
  };

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    // Robust base64 -> ArrayBuffer
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button className="chat-button rtl" onClick={handleOpen}>
          <img src="/chatbot-open-image%20(2).png" alt="Chat" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </button>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div className={`chat-widget ${isAnimating ? 'opening' : 'closing'} rtl`}>
          <div className="chat-header">
            <div className="header-avatar">
              <img src="/bot-avatar-60.png" alt="AI Assistant" />
            </div>
            <div className="header-info">
              <h3>Sanad AI</h3>
              <span className="status">{isVoiceMode ? 'متصل صوتياً' : 'متصل'}</span>
            </div>
            <div className="header-controls">
              <button 
                className="admin-btn" 
                title="لوحة الإدارة"
                onClick={() => {
                  window.history.pushState(null, '', '/admin');
                  // Trigger a custom event to notify App.tsx of the route change
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
              >
                ⚙️
              </button>
              <button className="close-button" onClick={handleClose}>
                ×
              </button>
            </div>
          </div>

          {!isVoiceMode && (
            <div className="chat-messages" ref={chatMessagesRef}>
              <div className="messages-container">
                {messages.map((message) => (
                  <div key={message.id} className={`message ${message.sender}`}>
                    <div className="message-wrapper">
                      {message.sender === 'bot' && (
                        <div className="avatar bot-avatar">
                          <img src="/bot-avatar-40.png" alt="Bot" />
                        </div>
                      )}
                      <div className="message-content" style={{
                        direction: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(message.text) ? 'rtl' : 'ltr',
                        textAlign: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(message.text) ? 'right' : 'left'
                      }}>
                        {message.sender === 'bot' ? (
                          message.isAnimating ? (
                            <TypewriterText 
                              text={message.text} 
                              speed={20}
                              onComplete={() => {
                                setMessages(prev => 
                                  prev.map(msg => 
                                    msg.id === message.id 
                                      ? { ...msg, isAnimating: false }
                                      : msg
                                  )
                                );
                              }}
                            />
                          ) : (
                            <div 
                              className="markdown-content" 
                              dir={(() => {
                                const arabicChars = (message.text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g) || []).length;
                                const totalChars = message.text.replace(/\s/g, '').length;
                                return arabicChars > totalChars * 0.3 ? 'rtl' : 'ltr';
                              })()} 
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdown(message.text)
                              }} 
                            />
                          )
                        ) : (
                          message.text
                        )}
                      </div>
                      {message.sender === 'user' && (
                        <div className="message-time">
                          {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="message bot">
                    <div className="message-wrapper">
                      <div className="avatar bot-avatar">
                        <img src="/bot-avatar-40.png" alt="Bot" />
                      </div>
                      <div className="message-content typing">
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          {isVoiceMode && (
            <div className="voice-mode-container">
              <button className="back-to-text-btn" onClick={disconnectVoice} title="العودة إلى المحادثة النصية">
                ←
              </button>
              <div className="voice-mode-content">
                <div className="voice-mode-avatar">
                  <img src="/bot-avatar-60.png" alt="AI Assistant" />
                </div>

                {isSpeaking && (
                  <div className="speaking-indicator-large">
                    <div className="speaking-animation-large">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <p>المساعد يتحدث...</p>
                  </div>
                )}

                {!isSpeaking && (
                  <div className="voice-mode-instructions">
                    <h3>المحادثة الصوتية</h3>
                    <p>{isRecording ? 'اضغط مرة أخرى لإرسال الرسالة' : 'اضغط على الزر لبدء التسجيل'}</p>
                  </div>
                )}

                <div className="voice-record-area-large">
                  <div className="voice-status-indicator">
                    {isConnected ? (
                      <div className={`connection-status connected ${isRecording ? 'listening' : ''}`}>
                        <div className="status-dot"></div>
                        <span>{isRecording ? 'يستمع...' : 'متصل'}</span>
                      </div>
                    ) : (
                      <div className="connection-status disconnected">
                        <div className="status-dot"></div>
                        <span>غير متصل</span>
                      </div>
                    )}
                  </div>

                  <div className="voice-controls">
                    <button 
                      className={`stop-bot-btn ${isSpeaking ? 'active' : 'inactive'}`}
                      onClick={handleUserInterrupt}
                      title="إيقاف المساعد"
                    >
                      ⏹ إيقاف المساعد
                    </button>
                    
                    {isSpeaking && (
                      <div className="interrupt-hint">
                        <p>تحدث بصوت عالٍ أو اضغط الزر لمقاطعة المساعد</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isVoiceMode && (
            <div className="chat-input">
              <div className="input-container">
                <button 
                  className={`voice-toggle-btn-inside ${isVoiceMode ? 'active' : ''}`}
                  onClick={isVoiceMode ? disconnectVoice : connectVoice}
                  title={isVoiceMode ? 'قطع الاتصال الصوتي' : 'اتصال صوتي'}
                >
                  {isVoiceMode ? '🔊' : <img src="/voice-chat-icon.webp" alt="Voice Chat" style={{ width: '18px', height: '18px' }} />}
                </button>
                <textarea
                  ref={textareaRef}
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="اكتب رسالتك..."
                  rows={1}
                  disabled={isLoading}
                />
              </div>
              <button onClick={sendMessage} disabled={isLoading}>
                ➤
              </button>
              <button 
                className={`mic-button ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isLoading}
              >
                {isRecording ? '⏹' : <img src="/mic-icon.png" alt="Mic" style={{ width: '20px', height: '20px' }} />}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};